import { Link } from 'react-router-dom';
import { SEO, SchemaMarkup } from '../components/seo';
import {
  ArrowRight,
  BadgeCheck,
  Clock,
  Euro,
  FileText,
  GraduationCap,
  Megaphone,
  Scale,
  ShieldCheck,
  Siren,
  Users,
} from 'lucide-react';

const FAQ_ITEMS = [
  {
    question: 'Is een vertrouwenspersoon wettelijk verplicht?',
    answer: 'Nee, er is op dit moment geen wet die een vertrouwenspersoon direct verplicht stelt. Het wetsvoorstel dat dit wilde regelen voor bedrijven vanaf 10 medewerkers is gestrand in de Eerste Kamer. Wel verplicht de Arbowet je om psychosociale arbeidsbelasting tegen te gaan. Bij een controle kan de Nederlandse Arbeidsinspectie een vertrouwenspersoon alsnog eisen, op last van een boete, als die risico\'s onvoldoende zijn afgedekt.',
  },
  {
    question: 'Wat is het verschil tussen een interne en externe vertrouwenspersoon?',
    answer: 'Een interne vertrouwenspersoon is een collega binnen de organisatie. Een externe vertrouwenspersoon staat volledig buiten de organisatie en heeft daardoor geen dubbele belangen. Voor medewerkers is de drempel om een externe vertrouwenspersoon te benaderen lager, zeker bij gevoelige onderwerpen als pesten, intimidatie of discriminatie.',
  },
  {
    question: 'Kunnen we een interne en externe vertrouwenspersoon combineren?',
    answer: 'Ja, en dat is vaak de sterkste opzet. De interne vertrouwenspersoon is het eerste aanspreekpunt voor alledaagse vragen en houdt voeling met de cultuur. De externe vertrouwenspersoon is het onafhankelijke kanaal voor alles wat gevoeliger ligt. Zo heeft elke medewerker een ingang die veilig voelt. Het online spreekuur staat bovendien ook open voor de interne vertrouwenspersoon zelf: die kan daar makkelijk en laagdrempelig ruggespraak houden met de externe vertrouwenspersoon van PuntUit.',
  },
  {
    question: 'Is een interne vertrouwenspersoon niet goedkoper dan een externe?',
    answer: 'Nee, vrijwel altijd juist duurder. De opleiding van een interne vertrouwenspersoon kost al snel €1.500 per persoon — ruim drie keer het complete PuntUit-jaarabonnement van €475. Daar komen de verleturen, de verplichte bij- en nascholing (al snel enkele honderden euro\'s per jaar) en de uren voor de rol zelf nog bij. En valt de opgeleide collega uit of vertrekt diegene, dan begint de investering opnieuw. In de praktijk is een externe vertrouwenspersoon daardoor vrijwel altijd én beter én goedkoper dan zelf een vertrouwenspersoon opleiden.',
  },
  {
    question: 'Wat kost een externe vertrouwenspersoon bij PuntUit?',
    answer: 'Je betaalt een vast jaarabonnement: €375 per jaar voor organisaties tot en met 10 personen en €475 per jaar voor 11 t/m 250 personen. Daar zit alles in: het gratis spreekuur, het online hulpvraagplatform, de Early Awareness Tool, het PuntUit-spel, de periodieke informatiemails en het geanonimiseerde jaarrapport. Alleen als een casus na het spreekuur verdere begeleiding vraagt, geldt een uurtarief van €84.',
  },
  {
    question: 'Geldt de Wet bescherming klokkenluiders ook voor ons?',
    answer: 'Heeft je organisatie 50 of meer medewerkers, dan moet je een interne meldregeling hebben voor vermoedens van misstanden. Een externe vertrouwenspersoon is daarbij een onafhankelijk aanspreekpunt waar medewerkers veilig terechtkunnen, los van de interne hiërarchie.',
  },
];

const COMPARISON_ROWS = [
  {
    label: 'Kosten',
    intern: 'Opleiding vanaf circa €1.500, plus bij- en nascholing (al snel enkele honderden euro\'s per jaar) en verleturen',
    extern: 'Vast jaarabonnement vanaf €375 per jaar, alles inbegrepen',
  },
  {
    label: 'Onpartijdigheid',
    intern: 'Risico op belangenverstrengeling en roddels op de werkvloer',
    extern: '100% onafhankelijk en anoniem, buiten de organisatie',
  },
  {
    label: 'Beschikbaarheid',
    intern: 'Kwetsbaar bij ziekte, vakantie en drukte met het eigen werk',
    extern: '24/7 via het online platform, persoonlijk bereikbaar op werkdagen van 9:00 tot 21:00',
  },
  {
    label: 'Kennis en ervaring',
    intern: 'Beperkte ervaring, omdat er intern weinig casussen voorbijkomen',
    extern: 'LVV-gecertificeerde professionals die dagelijks casussen behandelen',
  },
];

const PUNTUIT_FEATURES = [
  {
    icon: Siren,
    title: 'Early Awareness Tool en hulpvraagplatform',
    text: 'Elke organisatie krijgt een eigen online omgeving op praatuit.nl. Medewerkers stellen daar anoniem hun vraag, plannen direct een spreekuur in en vinden zelfhulpinformatie. De Early Awareness Tool signaleert spanningen voordat ze uitgroeien tot een melding.',
  },
  {
    icon: Megaphone,
    title: 'Doorlopende aandacht voor bewustwording en preventie',
    text: 'Met het PuntUit-spel maak je omgangsvormen op een laagdrempelige en leuke manier bespreekbaar op de werkvloer. Daarnaast ontvangen medewerkers periodieke informatiemails met korte video\'s over thema\'s rond omgangsvormen, zodat het onderwerp het hele jaar door blijft leven.',
  },
  {
    icon: FileText,
    title: 'Geanonimiseerd jaarrapport',
    text: 'Elk jaar ontvang je een geanonimiseerd rapport over wat er speelt in je organisatie. Dat rapport gebruik je direct voor de RI&E en als onderbouwing richting directie en ondernemingsraad.',
  },
  {
    icon: Euro,
    title: 'Vast tarief, geen verrassingen',
    text: 'Het abonnement is vanaf €475 per jaar voor organisaties van 11 t/m 250 medewerkers (tot en met 10 medewerkers €375). Het spreekuur is gratis; alleen verdere begeleiding bij een casus kost €84 per uur.',
  },
];

export function InterneVsExterneVP() {
  return (
    <>
      <SEO
        title="Externe vs. Interne Vertrouwenspersoon: Goedkoper én Beter"
        description="Een externe vertrouwenspersoon is vrijwel altijd én goedkoper én beter dan een interne. Bekijk de rekensom, de vergelijking en wat de wet echt verplicht."
        canonicalUrl="/interne-vs-externe-vp"
        ogType="article"
      />
      <SchemaMarkup
        type="article"
        pageUrl="https://puntuit.nl/interne-vs-externe-vp"
        pageTitle="Externe vs. Interne Vertrouwenspersoon: Goedkoper én Beter"
        pageDescription="Een externe vertrouwenspersoon is vrijwel altijd én goedkoper én beter dan een interne. Bekijk de rekensom, de vergelijking en wat de wet echt verplicht."
        articleMeta={{
          headline: 'Externe vs. Interne Vertrouwenspersoon: Goedkoper én Beter',
          description: 'Waarom een externe vertrouwenspersoon vrijwel altijd én goedkoper én beter is dan een interne: de rekensom, de vergelijking op onpartijdigheid, beschikbaarheid en ervaring, en een eerlijke uitleg van de wetgeving.',
          datePublished: '2026-03-01',
          dateModified: '2026-07-15',
          slug: 'interne-vs-externe-vp',
        }}
        breadcrumb={[
          { name: 'Home', url: '/' },
          { name: 'Interne vs. Externe VP', url: '/interne-vs-externe-vp' },
        ]}
      />
      <SchemaMarkup
        type="faqPage"
        faqItems={FAQ_ITEMS}
      />
      <div className="pt-28">
        {/* Hero */}
        <section className="relative bg-gradient-to-b from-sky-50 to-white py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="container max-w-4xl text-center">
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm font-medium text-gray-700">
                <BadgeCheck className="w-4 h-4 text-[#0284c7]" aria-hidden="true" />
                LVV-gecertificeerd
              </span>
              <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm font-medium text-gray-700">
                <Euro className="w-4 h-4 text-[#0284c7]" aria-hidden="true" />
                Vanaf €375 per jaar
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-semibold text-gray-900 mb-6">
              Externe vertrouwenspersoon: vrijwel altijd én goedkoper én beter dan intern
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-10 max-w-3xl mx-auto">
              Een collega opleiden tot interne vertrouwenspersoon klinkt praktisch, maar de rekensom en de praktijk wijzen dezelfde kant op. Alleen al de opleiding kost ruim drie keer het complete PuntUit-jaarabonnement, en op onafhankelijkheid, bereikbaarheid en ervaring wint extern het ook. We zetten het eerlijk naast elkaar, inclusief wat de wet wel en niet van je vraagt.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#E85D56] hover:bg-[#D14A43] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E85D56]"
              >
                Vraag een voorstel aan
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
              <Link
                to="/tarieven"
                className="inline-flex items-center justify-center px-6 py-3 border border-[#0284c7] text-base font-medium rounded-md text-[#0284c7] bg-transparent hover:bg-sky-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0284c7]"
              >
                Bekijk de tarieven
              </Link>
            </div>
          </div>
        </section>

        {/* Wetgeving: radicale eerlijkheid */}
        <section className="section-padding bg-white">
          <div className="container max-w-5xl">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">Eerst eerlijk: wat zegt de wet?</h2>
              <p className="text-gray-600 leading-relaxed">
                Veel aanbieders roepen dat een vertrouwenspersoon verplicht is. Dat klopt niet, en wij vinden dat je een keuze als deze op feiten hoort te maken. Dit is de stand van zaken.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="inline-flex items-center justify-center p-3 bg-sky-50 rounded-lg text-[#0284c7] mb-4">
                  <Scale className="w-6 h-6" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Geen directe verplichting</h3>
                <p className="text-gray-600 leading-relaxed">
                  Het wetsvoorstel dat een vertrouwenspersoon verplicht wilde stellen voor bedrijven vanaf 10 medewerkers is gestrand in de Eerste Kamer. Er is dus geen wet die je nu direct verplicht om er een aan te stellen.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="inline-flex items-center justify-center p-3 bg-sky-50 rounded-lg text-[#0284c7] mb-4">
                  <ShieldCheck className="w-6 h-6" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Wel de Arbowet en RI&E</h3>
                <p className="text-gray-600 leading-relaxed">
                  De Arbowet verplicht je om psychosociale arbeidsbelasting tegen te gaan. Dek je die risico's onvoldoende af, dan kan de Nederlandse Arbeidsinspectie bij een controle een vertrouwenspersoon alsnog eisen, op last van een boete.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="inline-flex items-center justify-center p-3 bg-sky-50 rounded-lg text-[#0284c7] mb-4">
                  <FileText className="w-6 h-6" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Wet bescherming klokkenluiders</h3>
                <p className="text-gray-600 leading-relaxed">
                  Vanaf 50 medewerkers moet je een interne meldregeling hebben. Een externe vertrouwenspersoon is daarbij het onafhankelijke aanspreekpunt waar medewerkers veilig terechtkunnen.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Vergelijkingstabel */}
        <section className="section-padding bg-gray-50">
          <div className="container max-w-5xl">
            <div className="max-w-3xl mb-10">
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">De vergelijking op een rij</h2>
              <p className="text-gray-600 leading-relaxed">
                We zetten de twee vormen eerlijk naast elkaar. De uitkomst: op elk criterium dat er voor je medewerkers en je begroting toe doet, wint de externe vertrouwenspersoon het vrijwel altijd.
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden overflow-x-auto">
              <table className="w-full text-left border-collapse" aria-label="Vergelijking interne en externe vertrouwenspersoon">
                <caption className="sr-only">Vergelijking tussen een interne vertrouwenspersoon en een externe vertrouwenspersoon van PuntUit op vier criteria.</caption>
                <thead>
                  <tr className="border-b-2 border-gray-100 bg-gray-50">
                    <th scope="col" className="p-5 text-gray-500 font-medium"><span className="sr-only">Criterium</span></th>
                    <th scope="col" className="p-5 font-semibold text-gray-700">Interne vertrouwenspersoon</th>
                    <th scope="col" className="p-5 font-semibold text-[#0284c7]">Externe vertrouwenspersoon (PuntUit)</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, index) => (
                    <tr key={row.label} className={index < COMPARISON_ROWS.length - 1 ? 'border-b border-gray-100' : undefined}>
                      <th scope="row" className="p-5 font-semibold text-left text-gray-900 align-top">{row.label}</th>
                      <td className="p-5 text-gray-500 align-top">{row.intern}</td>
                      <td className="p-5 text-gray-800 font-medium align-top">{row.extern}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <div className="bg-sky-50 rounded-xl p-6 border border-sky-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Én goedkoper</h3>
                <p className="text-gray-700 leading-relaxed">
                  Alleen al de opleiding van een interne collega kost circa €1.500 — ruim drie keer het complete jaarabonnement van €475. Tel daar de verleturen, de bij- en nascholing die de certificering elk jaar vraagt en de uren voor de rol zelf bij op, en het verschil loopt elk jaar verder op. De conclusie is simpel: een externe vertrouwenspersoon is in de praktijk vrijwel altijd én beter én goedkoper dan zelf een vertrouwenspersoon opleiden.
                </p>
              </div>
              <div className="bg-sky-50 rounded-xl p-6 border border-sky-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Én beter</h3>
                <p className="text-gray-700 leading-relaxed">
                  Een externe vertrouwenspersoon is aantoonbaar onafhankelijk, waardoor medewerkers eerder durven te melden. Je krijgt LVV-gecertificeerde professionals die dagelijks casussen behandelen — bereikbaar, ook bij ziekte, vakantie of vertrek.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Nadelen van puur intern */}
        <section className="section-padding bg-white">
          <div className="container max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div className="space-y-4 text-gray-700">
                <h2 className="text-3xl font-semibold text-gray-900">Waarom alleen intern vaak tegenvalt</h2>
                <p className="leading-relaxed">
                  De opleiding tot vertrouwenspersoon kost al snel €1.500 per persoon — ruim drie keer het complete PuntUit-jaarabonnement. Daar houdt het niet op: er komen verleturen bij, bij- en nascholing om de certificering geldig te houden en de uren die de rol naast het gewone werk vraagt. Valt die ene collega uit door ziekte of vertrek, dan begint de investering opnieuw.
                </p>
                <p className="leading-relaxed">
                  Het grootste bezwaar zit alleen niet in de kosten. Praten over ongewenst gedrag met een directe collega voelt voor veel medewerkers onveilig. Je komt diegene tegen bij de koffieautomaat, in het teamoverleg, misschien zelfs in de zaak waarover je wilt praten. Veel mensen zwijgen dan liever.
                </p>
                <p className="leading-relaxed">
                  Zo ontstaat een meldpunt dat op papier bestaat, maar in de praktijk nauwelijks wordt gebruikt. Problemen blijven sudderen tot ze escaleren in verzuim, verloop of een juridisch conflict.
                </p>
              </div>
              <div className="grid gap-4">
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 flex items-start gap-4">
                  <div className="inline-flex items-center justify-center p-3 bg-white rounded-lg text-[#0284c7] shrink-0">
                    <GraduationCap className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <p className="text-gray-600 leading-relaxed">De opleiding kost circa €1.500 per persoon, exclusief verleturen en de jaarlijkse bijscholing die de certificering vraagt.</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 flex items-start gap-4">
                  <div className="inline-flex items-center justify-center p-3 bg-white rounded-lg text-[#0284c7] shrink-0">
                    <Users className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <p className="text-gray-600 leading-relaxed">Melden bij een directe collega voelt onveilig. Zeker in kleinere teams houden medewerkers hun verhaal dan voor zich.</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 flex items-start gap-4">
                  <div className="inline-flex items-center justify-center p-3 bg-white rounded-lg text-[#0284c7] shrink-0">
                    <Clock className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <p className="text-gray-600 leading-relaxed">Bij ziekte, vakantie of vertrek van de interne vertrouwenspersoon staat het meldpunt stil, precies wanneer iemand het wil gebruiken.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Unieke voordelen PuntUit */}
        <section className="section-padding bg-gray-50">
          <div className="container max-w-6xl">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">Wat je bij PuntUit extra krijgt</h2>
              <p className="text-gray-600 leading-relaxed">
                Een <Link to="/externe-vertrouwenspersoon" className="underline underline-offset-2 decoration-primary-300 hover:decoration-primary-600">externe vertrouwenspersoon</Link> van PuntUit is meer dan een telefoonnummer voor als het misgaat. Het abonnement is gebouwd op preventie en zichtbaarheid op de werkvloer.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {PUNTUIT_FEATURES.map((feature) => (
                <div key={feature.title} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div className="inline-flex items-center justify-center p-3 bg-sky-50 rounded-lg text-[#0284c7] mb-4">
                    <feature.icon className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Social proof */}
        <section className="section-padding bg-white">
          <div className="container max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div className="space-y-4 text-gray-700">
                <h2 className="text-3xl font-semibold text-gray-900">Meer dan 500 organisaties gingen je voor</h2>
                <p className="leading-relaxed">
                  Van woningcorporaties tot bouwbedrijven en van zorginstellingen tot gemeentelijke organisaties: ruim 500 organisaties hebben hun vertrouwenspersoon bij PuntUit belegd. Vaak naast een interne vertrouwenspersoon, soms als enige meldpunt.
                </p>
                <p className="leading-relaxed">
                  Wat zij terugkrijgen is rust. Medewerkers hebben een veilig kanaal, HR is de dubbelrol kwijt en de directie heeft met het jaarrapport iets tastbaars voor de RI&E.
                </p>
              </div>
              <img
                src="/images/extern-vertrouwenspersoon.jpg"
                alt="Medewerker in vertrouwelijk gesprek met een externe vertrouwenspersoon"
                className="aspect-[4/3] rounded-xl object-cover w-full"
                loading="lazy"
                width={800}
                height={600}
              />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="section-padding bg-gray-50">
          <div className="container max-w-4xl">
            <h2 className="text-3xl font-semibold text-gray-900 mb-10 text-center">Veelgestelde vragen</h2>
            <dl className="space-y-6">
              {FAQ_ITEMS.map((item) => (
                <div key={item.question} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                  <dt className="text-lg font-semibold text-gray-900 mb-2">{item.question}</dt>
                  <dd className="text-gray-600 leading-relaxed m-0">{item.answer}</dd>
                </div>
              ))}
            </dl>
            <p className="text-center mt-8 text-gray-500">
              Meer lezen? Bekijk onze <Link to="/kennisbank" className="underline underline-offset-2 decoration-primary-300 hover:decoration-primary-600">kennisbank</Link> of onze pagina over <Link to="/preventie-ongewenst-gedrag" className="underline underline-offset-2 decoration-primary-300 hover:decoration-primary-600">preventie van ongewenst gedrag</Link>.
            </p>
          </div>
        </section>

        {/* Eind-CTA */}
        <section className="section-padding bg-[#0284c7] text-white">
          <div className="container text-center max-w-3xl">
            <h2 className="text-3xl font-semibold mb-4">Liever gewoon even overleggen?</h2>
            <p className="text-white/85 text-lg leading-relaxed mb-8">
              Vertel ons hoe jouw organisatie eruitziet, dan ontvang je binnen twee werkdagen een voorstel op maat. Geen verplichtingen, geen kleine lettertjes.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#E85D56] hover:bg-[#D14A43] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E85D56]"
              >
                Vraag een voorstel aan
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
              <Link
                to="/tarieven"
                className="inline-flex items-center justify-center px-6 py-3 border border-white text-base font-medium rounded-md text-white bg-transparent hover:bg-white/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
              >
                Bekijk de tarieven
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
